import { MatrixODCameraService } from '@shared/services/matrizOD-camera-service/matrix-od-camera.service';
import { EventEmitter, Inject, Injectable } from '@angular/core';
import { MapboxInstanceService } from '@sdk/services/mapbox-instance/mapbox-instance.service';
import { MapLayers } from '@shared/utils/mappers/layers-map.model';
import { GeometryTypeEnum, IMarkerBaseModel, MarkerModelBase } from '@types-custom/models/business/marker.model';
import * as mapboxgl from 'mapbox-gl';
import { AnySourceImpl, LngLat, Map, MapMouseEvent, Popup } from 'mapbox-gl';

import { BehaviorSubject, fromEvent, Subject, Subscription, throwError } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ParkingGeoTypeEnum } from '@shared/models/parking-zone.model';
import { getIconAsset, Icon } from '@types-custom/models/ui/icon-model';
import { ODMatrizService } from '@shared/services/odmatriz-service/odmatriz.service';
import {
  IDates,
  IFilteredSubscriber,
  IMatrix, IMatrixSensor,
  ODMatrixFilteredTypesEnum
} from '@shared/models/ODMatrix/ODMatrix.model';

export interface geoJsonType {
  features: any[];
  type: string;
}
@Injectable()
export class MapboxDataService {
  private readonly defaultLayer: string = 'default';
  private isMapReady = false;
  private isMapReady$: Subject<boolean> = new Subject();
  private layers: string[] = [];

  // OD Matrix Variables
  private SelectedMatrixSensor: IMatrixSensor | IMatrix | IDates
  private SelectedDatesMatrix: IMatrix | IDates | IMatrixSensor
  private SelectedMatrix: any;

  private Subscription1: Subscription;
  private Subscription2: Subscription;
  private Subscription3: Subscription;

  private cameraOD: Subscription;
  private cameraODFilter: Subscription;

  private selectFilterCameraOD:any = undefined
  private selectCameraOD:any

  markerBoxShadow: any;

  private linesClickDispatcher!: BehaviorSubject<any>;

  private dataSelectPmv = new BehaviorSubject({});
  selectpmv = this.dataSelectPmv.asObservable();

  mapClickEmitter = new EventEmitter<MapMouseEvent>();

  private get map(): Map {
    if (this.mapboxInstanceService.mapInstance) {
      return this.mapboxInstanceService.mapInstance;
    }
    throw new Error(`Mapbox instance hasn't been created.`);
  }

  constructor(
    private mapboxInstanceService: MapboxInstanceService,
    @Inject('environment') private environment: any,
    private odMatrixService: ODMatrizService,
    private odMatrixCameraService: MatrixODCameraService
  ) {
    this.Subscription2 = this.odMatrixService.filteredDatesSensor$.subscribe(
      (this) ? this.handleODMatrixInstance.bind(this) : null
    )
    this.Subscription3 = this.odMatrixService.filteredDeviceSensor$.subscribe(
      (this) ? this.handleODMatrixInstance.bind(this) : null
    )
    this.Subscription1 = this.odMatrixService.filteredSensor$.subscribe(
      (this) ? this.handleODMatrixInstance.bind(this) : null
    )

    this.cameraOD = this.odMatrixCameraService.dataCameraActive$.subscribe(
      (this) ? this.handleODMatrixInstance.bind(this) : null
    )
    this.cameraODFilter = this.odMatrixCameraService.dataCamerFilter$.subscribe(
      (this) ? this.handleODMatrixInstance.bind(this) : null
    )

    this.mapboxInstanceService.map$.subscribe(
      this.handleMapInstance.bind(this)
    );
  }

  public subscribeToMapStatus(callback: () => void) {
    if (!this.isMapReady) this.isMapReady$.subscribe(status => status && callback());
  }

  public getListMapLayers() {
    return this.layers;
  }

  private validateMap(): void {
    if (this.isMapReady) {
      return;
    }

    throwError('MapBox Map instance is not ready to be used');
  }

  private handleMapInstance(map: Map | undefined): void {
    if (map) {
      this.isMapReady = true;
      this.isMapReady$.next(true);

      this.setMapListeners();
    }
  }

  private setMapListeners(): void {
    fromEvent(this.map, 'click').subscribe(this.handleMapClick.bind(this));
  }

  public handleMapClick(event: MapMouseEvent): void {
    this.mapClickEmitter.next(event);
  }

  private buildDiv(marker: IMarkerBaseModel): HTMLDivElement {
    const wrapper: HTMLDivElement = document.createElement('div');
    const el: HTMLDivElement = document.createElement('div');
    el.className = marker.className;
    wrapper.appendChild(el);
    return wrapper;
  }

  private setListenersToNativeElement(
    el: HTMLElement,
    marker: IMarkerBaseModel
  ): HTMLElement {
    if (marker.unsubscribier) {
      fromEvent(el.childNodes[0], 'click') //TODO: check el.childNodes[0]
        .pipe(takeUntil(marker.unsubscribier))
        .subscribe(() => {
          marker.dispatcher?.next(marker.data);
        });
    }
    return el;
  }

  addClusterLayer(
    Source: {
      type: GeometryTypeEnum;
      markers: MarkerModelBase[];
      dataFeatures: any[];
    },
    layer: string,
    clickDispatcher: BehaviorSubject<any>,
    layerVisibility: boolean = true
  ): void {
    this.validateMap();

    if (this.layers.includes(layer)) return;
    if (this.markerBoxShadow) {
      this.markerBoxShadow.remove();
    }
    this.linesClickDispatcher = clickDispatcher;

    const layoutIconMap: { [key: string]: string | any } = {
      'Accident': 'map_accident',
      'ParkingSquarePMR': [
        'match',
        ['get', ['string', 'status']],
        'free', 'PMR_Disponible',
        'occupied', 'PMR_Ocupado',
        null, 'PMR_Indefinido',
        'PMR_Indefinido'
      ],
      'ParkingZone': [
        'match',
        ['get', ['string', 'status']],
        'free', 'parking_state_free',
        'occupied', 'parking_state_occupied',
        'closed', 'parking_state_unknown',
        'parking_state_unknown'
      ]
    };

    this.map.addSource(layer, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: Source.dataFeatures,
      },
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 52,
    });


    const layerCluster = layer.includes('-Cluster') ? layer : `${layer}-Cluster`;


    this.map.addLayer({
      id: layerCluster,
      type: 'symbol',
      source: layer,
      filter: ['has', 'point_count'],
      layout: {
        'icon-image': 'map_cluster_parking',
        'icon-size': 0.36,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      }
    });

    this.map.addLayer({
      id: `${layer}-Count`,
      type: 'symbol',
      source: layer,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 16,
      },
      paint: {
        "text-color": '#FFF'
      }
    });

    this.map.addLayer({
      id: layer,
      type: 'symbol',
      source: layer,
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': layoutIconMap[layer] ?? ['get', 'icon'],
        'icon-size': 0.15,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });

    this.map.on('mouseenter', layer, (e) => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', layer, () => {
      this.map.getCanvas().style.cursor = '';
    });
    this.setPopup(layer);

    this.map.on('click', layer, (event) =>
      this.handleLineClick(event, Source.markers[0].className)
    );

    this.map.on('click', layerCluster, (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: [layerCluster],
      });
      const clusterId = features[0].properties.cluster_id;
      (this.map.getSource(layer) as any).getClusterExpansionZoom(
        clusterId,
        (err: any, zoom: any) => {
          if (err) return;

          this.map.easeTo({
            center: (features[0].geometry as any).coordinates,
            zoom: zoom,
          });
        }
      );
    });

    this.layers.push(layer);
    this.setLayerVisibility(layer, layerVisibility);

  }

  addClusterLayerChart(
    Source: {
      type: GeometryTypeEnum;
      markers: MarkerModelBase[];
      dataFeatures: any[];
    },
    layer: string, //Locations
    clickDispatcher: BehaviorSubject<any>,
    layerVisibility: boolean = true
  ): void {
    this.validateMap();


    if (this.layers.includes(layer)) return;


    this.linesClickDispatcher = clickDispatcher;

    const layoutIconMap: { [key: string]: string | any } = {
      'Accident': 'map_accident',
      'ParkingSquarePMR': [
        'match',
        ['get', ['string', 'status']],
        'free', 'PMR_Disponible',
        'occupied', 'PMR_Ocupado',
        'closed', 'PMR_Indefinido',
        'PMR_Indefinido'
      ],
      'ParkingZone': [
        'match',
        ['get', ['string', 'status']],
        'free', 'parking_state_free',
        'occupied', 'parking_state_occupied',
        'closed', 'parking_state_unknown',
        'parking_state_unknown'
      ]
    };

    const priority0 = ['==', ['get', 'status'], 'occupied'];
    const priority1 = ['==', ['get', 'status'], 'free'];
    const priority2 = ['==', ['get', 'status'], 'closed'];
    const priority3 = ['==', ['get', 'status'], 'unknown'];

    const colors = ['#fa463d', '#428dff', '#00C479', '#D2D2D2', 'rgba(255,255,255,0)'];

    this.map.addSource(layer, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: Source.dataFeatures,
      },
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
      clusterProperties: {
        // Keep separate counts for each priority_name category in a cluster
        'priority0': ['+', ['case', priority0, 1, 0]],
        'priority1': ['+', ['case', priority1, 1, 0]],
        'priority2': ['+', ['case', priority2, 1, 0]],
        'priority3': ['+', ['case', priority3, 1, 0]],
      }
    });

    const layerCluster = `${layer}-Cluster`;

    this.map.addLayer({

      id: layerCluster,
      type: 'circle',
      source: layer,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'case',
          priority0,
          colors[0],
          priority1,
          colors[1],
          priority2,
          colors[2],
          priority3,
          colors[3],
          colors[4]
        ],
        'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
      },
    });
    const layerCount = `${layer}-Count`;

    this.map.addLayer({
      id: layerCount,
      type: 'symbol',
      source: layer,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
      paint: {
        'text-color': 'white',
      },
    });

    this.map.addLayer({
      id: layer,
      type: 'symbol',
      source: layer,
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': layoutIconMap[layer] ?? ['get', 'icon'],
        'icon-size': 0.15,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });

    this.map.on('mouseenter', layer, (e) => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', layer, () => {
      this.map.getCanvas().style.cursor = '';
    });
    this.setPopup(layer);

    this.map.on('click', layer, (event) =>
      this.handleLineClick(event, Source.markers[0].className)
    );

    this.map.on('click', layerCluster, (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: [layerCluster],
      });
      const clusterId = features[0].properties.cluster_id;
      (this.map.getSource(layer) as any).getClusterExpansionZoom(
        clusterId,
        (err: any, zoom: any) => {
          if (err) return;

          this.map.easeTo({
            center: (features[0].geometry as any).coordinates,
            zoom: zoom,
          });
        }
      );
    });

    this.layers.push(layerCluster);
    this.layers.push(layerCount);
    this.layers.push(layer);
    this.setLayerVisibility(layer, layerVisibility);

    const markers: any = {};
    let markersOnScreen: any = {};

    const updateMarkers = () => {
      const newMarkers: any = {};
      const features = this.map.querySourceFeatures(layer);

      // for every cluster on the screen, create an HTML marker for it (if we didn't yet),
      // and add it to the map if it's not there already
      for (const feature of features) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const coords = feature.geometry.coordinates;
        const props = feature.properties;
        if (!props.cluster) continue;
        const id = props.cluster_id;

        let marker = markers[id];
        if (!marker) {
          const el = createDonutChart(props);
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          marker = markers[id] = new mapboxgl.Marker({
            element: el
          }).setLngLat(coords);
        }
        newMarkers[id] = marker;

        if (!markersOnScreen[id]) marker.addTo(this.map);
      }
      // for every marker we've added previously, remove those that are no longer visible
      for (const id in markersOnScreen) {
        if (!newMarkers[id]) markersOnScreen[id].remove();
      }

      if (!this.map.getLayer(layer)) {
        for (const id in newMarkers) {
          newMarkers[id].remove();
        }
      }
      markersOnScreen = newMarkers;
    }

    this.map.on('render', () => {
      if (this.map.getLayer(layer)) {
        if (!this.map.isSourceLoaded(layer)) return;
        updateMarkers();
      } else {
        updateMarkers();
      }

    });

    function createDonutChart(props: { [x: string]: any; priority0?: any; priority1?: any; priority2?: any; priority3?: any; }) {
      const offsets = [];
      const counts = [
        props.priority0,
        props.priority1,
        props.priority2,
        props.priority3
      ];
      let total = 0;
      for (const count of counts) {
        offsets.push(total);
        total += count;
      }
      const fontSize =
        total >= 1000 ? 22 : total >= 100 ? 20 : total >= 10 ? 18 : 16;
      const r =
        total >= 1000 ? 50 : total >= 100 ? 32 : total >= 10 ? 24 : 18;
      const r0 = Math.round(r * 0.6);
      const w = r * 2;

      let html = `<div>
<svg width="${w}" height="${w}" viewbox="0 0 ${w} ${w}" text-anchor="middle" style="font: ${fontSize}px sans-serif; display: block" color="white">`;

      for (let i = 0; i < counts.length; i++) {
        html += donutSegment(
          offsets[i] / total,
          (offsets[i] + counts[i]) / total,
          r,
          r0,
          colors[i]
        );
      }
      html += `<circle cx="${r}" cy="${r}" r="${r0}" fill="#1A1F26F2" />
<text dominant-baseline="central" transform="translate(${r}, ${r})" fill="white">
${total.toLocaleString()}
</text>
</svg>
</div>`;

      const el = document.createElement('div');
      el.innerHTML = html;
      return el.firstChild;
    }

    function donutSegment(start: number, end: number, r: number, r0: number, color: string) {
      if (end - start === 1) end -= 0.00001;
      const a0 = 2 * Math.PI * (start - 0.25);
      const a1 = 2 * Math.PI * (end - 0.25);
      const x0 = Math.cos(a0),
        y0 = Math.sin(a0);
      const x1 = Math.cos(a1),
        y1 = Math.sin(a1);
      const largeArc = end - start > 0.5 ? 1 : 0;

      // draw an SVG path
      return `<path d="M ${r + r0 * x0} ${r + r0 * y0} L ${r + r * x0} ${r + r * y0
        } A ${r} ${r} 0 ${largeArc} 1 ${r + r * x1} ${r + r * y1} L ${r + r0 * x1
        } ${r + r0 * y1} A ${r0} ${r0} 0 ${largeArc} 0 ${r + r0 * x0} ${r + r0 * y0
        }" fill="${color}" />`;
    }
  }

  addStatesLayer(
    Source: {
      type: GeometryTypeEnum;
      markers: MarkerModelBase[];
      dataFeatures: any[];
    },
    layer: string,
    clickDispatcher: BehaviorSubject<any>,
    layerVisibility: boolean = true
  ): void {
    this.validateMap();
    const mapboxLayerId = `${layer}-States`;
    if (this.layers.includes(mapboxLayerId)) {
      this.removeLayerFromList(mapboxLayerId);
      if (this.map.getSource(mapboxLayerId)) {
        this.map.removeSource(mapboxLayerId);
      }
    }
    if (this.markerBoxShadow) {
      this.markerBoxShadow.remove();
    }
    this.linesClickDispatcher = clickDispatcher;

    this.map.addSource(mapboxLayerId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: Source.dataFeatures,
      },
    });

    // Add a layer showing the state polygons.
    // const layerPolygonId = `${layer}-Polygon`;

    this.map.addLayer({
      'id': mapboxLayerId,
      'type': 'fill',
      'source': mapboxLayerId,
      'paint': {
        'fill-color': this.getFilterColorFill(layer),
        'fill-opacity': 0.15
      }
    });

    const layerLineId = `${layer}-Lines`;

    this.map.addLayer({
      'id': layerLineId,
      'type': 'line',
      'source': mapboxLayerId,
      'paint': {
        'line-color': this.getFilterColorFill(layer),
        'line-width': 1.5
      }
    });


    let popup!: any;

    this.map.on('mouseenter', mapboxLayerId, e => {
      this.map.getCanvas().style.cursor = 'pointer';

      if (e.features[0].layer.id == 'ParkingZoneLoading-States') {
        popup = new Popup({ closeButton: false, closeOnClick: true, className: '' })
          .setLngLat(e.lngLat)
          .setHTML(e.features[0].properties.name)
          .addTo(this.map);
      }

    });

    this.map.on('mouseleave', mapboxLayerId, () => {
      this.map.getCanvas().style.cursor = '';
      popup.remove();
    });

    // this.map.on('mousemove', mapboxLayerId, () => {
    //   this.map.getCanvas().style.cursor = '';
    //   popup.remove();
    // });

    this.map.on('click', mapboxLayerId, (event) => {

      this.handleLineClick(event, Source.markers[0].className);
      if (this.markerBoxShadow) {
        this.markerBoxShadow.remove();
      }
      if (event.features[0].geometry.type === 'Point') {
        const el = document.createElement("div");
        if (['TrafficLight', 'ParkingLot', 'TrafficManagmentPlans', 'StopBus', 'Zones', 'Cameras', 'Locations'].includes(event.features[0].layer.id)) {
          el.className = "marker-active-static-layer";
        }
        else if ((event.features[0].layer.id) == 'AlertsWaze') {
          el.className = "marker-active-situations-layer";
        }
        else {
          el.className = "marker-active"
        }
        this.markerBoxShadow = new mapboxgl.Marker(el)
          .setLngLat([event.features[0].geometry.coordinates[0], event.features[0].geometry.coordinates[1]])
          .addTo(this.map);
      }
    });

    this.layers.push(layerLineId);
    this.layers.push(mapboxLayerId);
    this.setLayerVisibility(mapboxLayerId, layerVisibility);
  }

  addHeatLayer(
    Source: {
      type: GeometryTypeEnum;
      markers: MarkerModelBase[];
      dataFeatures: any[];
    },
    layer: string,
    clickDispatcher: BehaviorSubject<any>,
    layerVisibility: boolean = true
  ): void {
    this.validateMap();

    const mapboxLayerId = `${layer}-Heat`;
    if (this.layers.includes(mapboxLayerId)) return;
    if (this.markerBoxShadow) {
      this.markerBoxShadow.remove();
    }
    this.linesClickDispatcher = clickDispatcher;

    this.map.addSource(mapboxLayerId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: Source.dataFeatures,
      },
    });

    this.map.addLayer({
      id: mapboxLayerId,
      type: 'heatmap',
      source: mapboxLayerId,
      maxzoom: 24,
      paint: {
        'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 1, 29, 3],

        'heatmap-color': [
          'interpolate',
          ['linear'],
          ['heatmap-density'],
          0,
          'rgba(33,102,172,0)',
          0.2,
          'rgb(103,169,207)',
          0.4,
          'rgb(209,229,240)',
          0.6,
          'rgb(253,219,199)',
          0.8,
          'rgb(239,138,98)',
          1,
          'rgb(178,24,43)',
        ],

        'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 2, 24, 40],

        'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 7, 1, 24, 0],
      },
    });

    this.layers.push(mapboxLayerId);
    this.setLayerVisibility(mapboxLayerId, layerVisibility);
  }

  markPoint(lat: number, lng: number) {
    if (this.markerBoxShadow) {
      this.markerBoxShadow.remove();
    }
    const el = document.createElement("div");
    el.className = "marker-active-situations-layer";
    this.markerBoxShadow = new mapboxgl.Marker(el)
      .setLngLat([lng, lat])
      .addTo(this.map);
  }

  addPointLayer(
    Source: {
      type: GeometryTypeEnum;
      markers: MarkerModelBase[];
      dataFeatures: any[];
    },
    layer: string,
    clickDispatcher: BehaviorSubject<any>,
    layerVisibility = true
  ): void {
    this.validateMap();
    if (this.layers.includes(layer)) return;

    if (this.markerBoxShadow) {
      this.markerBoxShadow.remove();
    }
    this.linesClickDispatcher = clickDispatcher;
    this.map.addSource(layer, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: Source.dataFeatures,
      },
    });

    this.map.addLayer({
      id: layer,
      type: 'symbol',
      source: layer,
      layout: {
        'icon-image': layer === 'action' ? 'pointer_location' : ['get', 'icon'],
        'icon-size': layer === 'Garbage' ? 0.8 : 0.25,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });
    this.setPopup(layer);
    if (!['action', 'Skateboards'].includes(layer)) {
      this.map.on('click', layer, (event) => {
        this.handleLineClick(event, Source.markers[0].className);
        if (this.markerBoxShadow) {
          this.markerBoxShadow.remove();
        }
        if (event.features[0].geometry.type === 'Point') {
          const el = document.createElement("div");

          el.className = "marker-active"
          Object.assign(el.style, {
            // filter: "drop-shadow(0 0 0.75em white)",
            width: "2.5rem",
            height: "2.5rem",
            backgroundRepeat: "no-repeat",
            backgroundSize: 'cover',
            transformOrigin: "center",
            transition: "transform .2s ease-in-out",
            // animation: "scaleUp 0.3s ease-in-out forwards"
          });

          const data = Icon[event.features[0].properties.icon].indexOf('_ng') > -1
            ? Icon.defaultIcon
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            : Icon[Icon[event.features[0].properties.icon.toString()]];
          el.style.backgroundImage = `url(${getIconAsset(data)})`;

          this.markerBoxShadow = new mapboxgl.Marker(el)
            .setLngLat([event.features[0].geometry.coordinates[0], event.features[0].geometry.coordinates[1]])
            .addTo(this.map);

          const initialTransform = this.markerBoxShadow.getElement().style.transform;

          this.markerBoxShadow.getElement().style.transform = `${initialTransform} scale(.1)`;
          setTimeout(() => {
            this.markerBoxShadow.getElement().style.transform = `${initialTransform} scale(1)`;
            this.markerBoxShadow.getElement().style.filter = 'drop-shadow(0 0 1em white)';
            setTimeout(() => {
              this.markerBoxShadow.getElement().style.transition = '';
            }, 250);
          }, 0);
        }
      });
    }
    this.layers.push(layer);
    this.setLayerVisibility(layer, layerVisibility);
  }

  addLines(
    Source: {
      type: GeometryTypeEnum;
      markers: MarkerModelBase[];
      dataFeatures: any[];
    },
    layer: string,
    clickDispatcher?: BehaviorSubject<any>,
    layerVisibility: boolean = true,
    underLayerId?: string
  ): any {
    this.validateMap();
    if (this.markerBoxShadow) {
      this.markerBoxShadow.remove();
    }
    this.linesClickDispatcher = clickDispatcher;
    const mapboxLayerId = layer.includes('-Lines') ? layer : `${layer}-Lines`;

    if (this.map.getLayer(mapboxLayerId)) {
      this.map.removeLayer(mapboxLayerId);
    }

    if (this.map.getSource(mapboxLayerId)) {
      this.map.removeSource(mapboxLayerId);
    }

    this.map.addSource(mapboxLayerId, {
      type: 'geojson',
      lineMetrics: this.setLineMetrics(layer),
      data: {
        type: 'FeatureCollection',
        features: Source.dataFeatures,
      },
    });

    const addLayerOpts = {
      id: mapboxLayerId,
      type: 'line',
      source: mapboxLayerId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': this.getFilterColor(layer),
        'line-width': this.getLineWidth(layer),
      },
    }
    if (underLayerId)
      this.map.addLayer(addLayerOpts as mapboxgl.AnyLayer, underLayerId);
    else
      this.map.addLayer(addLayerOpts as mapboxgl.AnyLayer);

    if (layer === 'AgentsHistorical') {
      this.map.setPaintProperty(
        mapboxLayerId,
        'line-gradient',
        this.getLineGradient()
      );
    }

    if (

      layer === 'Aforo' ||
      layer === 'BusZone-Lines' ||
      layer === 'BicyclePath' ||
      layer === 'RoadRun' ||
      layer === 'TrafficSpeedRange' ||
      layer === 'TrafficConcordance' ||
      layer === 'ParkingZone'
    ) {
      this.setPopup(mapboxLayerId);
    }

    this.map.on('click', mapboxLayerId, (event) =>
      this.handleLineClick(event, Source.markers[0].className)
    );

    this.layers.push(mapboxLayerId);
    this.setLayerVisibility(mapboxLayerId, layerVisibility);
  }

  public updateLayer(layer: string) {
    return;
  }

  clearLayer(layer: string): void {
    let optionalLineLayer = '';
    if (
      layer === 'Traffic' ||
      layer === 'TrafficSpeedRange' ||
      layer === 'TrafficConcordance' ||
      // layer === 'BicyclePath' ||
      // layer === 'BusZone' ||
      layer === 'StopBus' ||
      layer === 'RoadRun'
    ) {
      layer = layer + '-Lines';
    }

    if (
      layer === 'BusZone'
    ) {
      optionalLineLayer = layer + '-Lines';
    }

    if (layer === MapLayers.ParkingZone || layer === MapLayers.ParkingSquarePMR) {
      optionalLineLayer = layer + '-underground';
      // this.removeLayer(layer + '-Lines');
    }

    this.removeLayer(layer);
    if (optionalLineLayer)
      this.removeLayer(optionalLineLayer);
  }

  private removeLayer(layer: string) {
    if (!this.map.getLayer(layer)) return;
    this.validateMap();
    this.map.off('click', layer, (e) => this.handleLineClick(e));
    this.removeLayerFromList(layer);
    this.map.removeLayer(layer);

    if (layer === 'Accident') {
      this.map.removeLayer(layer + '-Cluster');
      this.map.removeLayer(layer + '-Count');
    }

    if (layer === MapLayers.ParkingZoneLoading) {
      if (this.map.getLayer(`${layer}-Count`))
        this.map.removeLayer(`${layer}-Count`);
      if (this.map.getLayer(`${layer}-Cluster`))
        this.map.removeLayer(`${layer}-Cluster`);
      if (this.map.getLayer(`${layer}-States`))
        this.map.removeLayer(`${layer}-States`);
      if (this.map.getLayer(`${layer}-Lines`))
        this.map.removeLayer(`${layer}-Lines`);
    }

    if (layer === MapLayers.ParkingZone || layer === MapLayers.ParkingSquarePMR) {
      if (this.map.getLayer(`${layer}-Count`))
        this.map.removeLayer(`${layer}-Count`);
      if (this.map.getLayer(`${layer}-Cluster`))
        this.map.removeLayer(`${layer}-Cluster`);
      if (this.map.getLayer(`${layer}-Lines`))
        this.map.removeLayer(`${layer}-Lines`);
    }

    if (this.map.getSource(layer))
      this.map.removeSource(layer);
  }

  clearMap(): void {
    const LayersToDelete = [...this.layers];
    LayersToDelete.forEach((layer) => {
      this.clearLayer(layer);
    });
    this.markerBoxShadow && this.markerBoxShadow.remove()
  }

  removeLayerFromList(layer: string) {
    const index = this.layers.indexOf(layer, 0);
    if (index > -1) {
      this.layers.splice(index, 1);
    }
  }

  lineclick(data: { event: any, layer: string }) {
    return this.handleLineClick(data.event, data.layer);
  }

  private handleLineClick(event: any, classLayerName?: string): void {
    const pseudoImarkerClass = {
      className: classLayerName,
      classProperties: event.features[0].properties,
    };

    if (classLayerName == 'PmvModel') {
      this.dataSelectPmv.next(pseudoImarkerClass);
      this.linesClickDispatcher.next(pseudoImarkerClass);
    }

    if (classLayerName === 'MatrixODModel') {
      this.odMatrixService.sensorFilterDeviceSubject.next({
        type: ODMatrixFilteredTypesEnum.SENSOR,
        values: {
          ...pseudoImarkerClass.classProperties,
          coordinates: (pseudoImarkerClass.classProperties.coordinates.replace(/[\\[\]]/g, '').split(',')).map((coord: any) => parseFloat(coord))
        }
      })

    } if (classLayerName === 'MatrixODCameraModel') {
      this.odMatrixCameraService.cameraDataActive.next({
        type: ODMatrixFilteredTypesEnum.CAMERA,
        values: {
          ...pseudoImarkerClass.classProperties,
          coordinates: (pseudoImarkerClass.classProperties.coordinates.replace(/[\\[\]]/g, '').split(',')).map((coord: any) => parseFloat(coord))
        }
      })
    }
    else {
      this.linesClickDispatcher.next(pseudoImarkerClass);
    }
  }

  private handleODMatrixInstance(object: IFilteredSubscriber) {
    if (!object) return
    if (object?.type !== ODMatrixFilteredTypesEnum.CAMERA && object?.type !== ODMatrixFilteredTypesEnum.CAMERA_FILTER && object?.type !== ODMatrixFilteredTypesEnum.CAMERA_PEDIOD) {
      if (object.type === ODMatrixFilteredTypesEnum.SENSOR) {
        this.SelectedMatrixSensor = object.values;
      }
      if (object.type === ODMatrixFilteredTypesEnum.MATRIX) {
        this.SelectedMatrix = object.values
      }
      if (object.type === ODMatrixFilteredTypesEnum.DATES) {
        this.SelectedDatesMatrix = object.values
      }

      if (this.SelectedMatrixSensor) {
        this.odMatrixService.getDataSinceSensorBySensorID(
          this.SelectedMatrixSensor ? this.SelectedMatrixSensor : undefined,
          this.SelectedMatrix ? this.SelectedMatrix : undefined,
          this.SelectedDatesMatrix ? this.SelectedDatesMatrix : undefined,
        ).subscribe((res: any) => {
          const markers: any[] = []
          const dataFeatures: any[] = []

          this.odMatrixService.sensorActiveSubject.next(res.features[0].properties)

          res.features[0].properties.sensor.map((marker: any) => {
            if ('coordinates' in this.SelectedMatrixSensor) {
              marker = {
                properties: marker,
                geometry: {
                  type: "LineString",
                  coordinates: [
                    this.SelectedMatrixSensor.coordinates,
                    [marker.longitude, marker.latitude]
                  ]
                }
              }
            }

            markers.push(
              {
                geometry: marker.geometry,
                classProperties: {
                  ...marker.properties,
                  originName: res.features[0].properties.typeSensor
                },
                className: 'MatrixODModel',
              }
            )
            dataFeatures.push(
              {
                geometry: marker.geometry,
                properties: {
                  ...marker.properties,
                  originName: res.features[0].properties.typeSensor
                },
                type: "Feature"
              }
            )
          })


          const layers = {
            type: GeometryTypeEnum.LineString,
            markers: markers,
            dataFeatures: dataFeatures
          }

          this.addLines(layers, 'MatrixOD')

        })
      }
    } else {
      const markers: any[] = [];
      const dataFeatures: any[] = [];

      if(object.type === ODMatrixFilteredTypesEnum.CAMERA){
        this.selectCameraOD = object.values
      }

      if(object.type === ODMatrixFilteredTypesEnum.CAMERA_FILTER as ODMatrixFilteredTypesEnum){
        this.selectFilterCameraOD = object.values.percentage
      }

      if (object.type === ODMatrixFilteredTypesEnum.CAMERA_PEDIOD as ODMatrixFilteredTypesEnum) {
        this.SelectedDatesMatrix = object.values.timestamp
      }
      const body = {
        cameraNlId: this.selectCameraOD.cameraNlId,
        period: this.SelectedDatesMatrix ? this.SelectedDatesMatrix : '1h',
        percentages: [
          {
            "start": this.selectFilterCameraOD ? this.selectFilterCameraOD.min : 0,
            "end": this.selectFilterCameraOD ? this.selectFilterCameraOD.max : 100,
          }
        ]
      }
      this.odMatrixCameraService.getCameraFromPointCamera(body).subscribe({
        next: (response: any) => {
          const listPanel = response.features[0]?.properties

          this.odMatrixCameraService.cameraDataSubjet.next(listPanel);

          response.features[0].properties.destinations.map((marker: any) => {

            marker = {
              properties: marker,
              geometry: {
                type: "LineString",
                coordinates: [
                  this.selectCameraOD.coordinates,
                  [marker.longitude, marker.latitude]
                ]
              }
            }
            markers.push(
              {
                geometry: marker.geometry,
                classProperties: {
                  ...marker,
                  originName: marker.name
                },
                className: 'MatrixODModel',
              }
            )
            dataFeatures.push(
              {

                geometry: marker.geometry,
                properties: {
                  ...marker.properties,
                  originName: response.address
                },
                type: "Feature"
              }
            )
          })


          const layers = {
            type: GeometryTypeEnum.LineString,
            markers: markers,
            dataFeatures: dataFeatures
          }

          this.addLines(layers, 'MatrixOD-camera')

        }
      })
    }

  }

  getFilterColor(layer: string): any | string {
    const filterArray = ['case'];
    let defaultColorArray = ['#428DFF'];
    // const propFilter = ['get', 'levelOfService'];
    const filterStatus = ['boolean', ['feature-state', 'hover'], false];

    if (layer === 'TrafficSpeedRange') {
      return [
        'get', 'typeColor',
      ];
    }

    if (layer === 'TrafficConcordance') {
      const propFilterCooncordance = [
        'step',
        ['get', 'concordance'],
        '#FF4545', -50,
        '#FF6F6F', -40,
        '#FF6F6F', -30,
        '#FF6F6F', -25,
        '#FF6F6F', -24,
        '#FFBBBB', -20,
        '#FFBBBB', -11,
        '#FFBBBB', -10,
        '#5C9BFB', 0,
        '#5C9BFB', 10,
        '#5C9BFB', 11,
        '#D6FFD6', 20,
        '#D6FFD6', 24,
        '#D6FFD6', 25,
        '#7FF57D', 30,
        '#7FF57D', 40,
        '#7FF57D', 50,
        '#7FF57D', 51,
        '#2AC527'
      ];
      return [
        ...filterArray,
        ...[filterStatus],
        ...defaultColorArray,
        ...[propFilterCooncordance],
      ];
    }

    if (layer === 'RoadRun') {
      defaultColorArray = ['#B2FAFF'];
    }

    if (layer === `${MapLayers.BusZone}-Lines`) {
      return ['get', 'color'];
    }

    const ODMatrixRangeConfig = (originName: string, ranges: [number, string][]) => {
      const config = [['==', ['get', 'originName'], originName]];
      const conditionsAndColors = [];

      conditionsAndColors.push('case');
      ranges.forEach(([percentage, color]) => {
        conditionsAndColors.push(['<=', ['get', 'percentage'], percentage], color);
      });

      conditionsAndColors.push('#FFFFFF');
      config.push([...conditionsAndColors]);
      return config;
    };

    if (layer === 'MatrixOD') {
      const propFilterODMatrixRange = [
        'case',
        ...ODMatrixRangeConfig('ARRIVALS', [
          [0, '#fff4c6'],
          [11, '#ffe36a'],
          [21, '#fecb38'],
          [31, '#ffb618'],
          [41, '#f99e00'],
          [51, '#e88700'],
          [61, '#ff6f06'],
          [71, '#e75700'],
          [81, '#f04d00'],
          [91, '#ed3900'],
          [Infinity, '#ed3900'],
        ]),
        ...ODMatrixRangeConfig('DEPARTURES', [
          [0, '#d6f3ff'],
          [11, '#a9deff'],
          [21, '#90cbff'],
          [31, '#75b5ff'],
          [41, '#5095ff'],
          [51, '#4484ff'],
          [61, '#326dff'],
          [71, '#285bff'],
          [81, '#1642ff'],
          [91, '#2400ff'],
          [Infinity, '#2400ff'],
        ]),
        '#FFFFFF',
      ];

      return [
        ...filterArray,
        ...[filterStatus],
        ...defaultColorArray,
        ...[propFilterODMatrixRange],
      ];
    }

    if (layer.startsWith('MatrixOD-camera')) {
      const propFilterODMatrixRange = [
        "case",
        ["<=", ["get", "percentage"], 0], "#fff4c6",
        ["<=", ["get", "percentage"], 11], "#ffe36a",
        ["<=", ["get", "percentage"], 21], "#fecb38",
        ["<=", ["get", "percentage"], 31], "#ffb618",
        ["<=", ["get", "percentage"], 41], "#f99e00",
        ["<=", ["get", "percentage"], 51], "#e88700",
        ["<=", ["get", "percentage"], 61], "#ff6f06",
        ["<=", ["get", "percentage"], 71], "#e75700",
        ["<=", ["get", "percentage"], 81], "#f04d00",
        ["<=", ["get", "percentage"], 91], "#ed3900",
        "#ed3900"
      ]

      return [
        ...filterArray,
        ...[filterStatus],
        ...defaultColorArray,
        // ...['red'],
        ...[propFilterODMatrixRange],
      ];
    }


    if (layer === 'ParkingZone') {
      const propFilterParking = [
        'case',
        ['==', ['get', 'category'], 'blueZone'], '#458EFB',
        ['==', ['get', 'category'], 'greenZone'], '#04C200',
        "#FFFFFF"
      ];
      return [
        ...filterArray,
        ...[filterStatus],
        ...defaultColorArray,
        ...[propFilterParking],
      ];
    }

    return defaultColorArray[0];
  }

  getFilterColorFill(layer: string): any | string {
    const filterArray = ['case'];
    const defaultColorArray = ['#428DFF'];
    const filterStatus = ['boolean', ['feature-state', 'hover'], false];

    const propFilterZoneLoading = [
      'case',
      ['==', ['get', 'name'], 'Zona 1'], '#6BFF7A',
      ['==', ['get', 'name'], 'Zona 2'], '#56CFFF',
      ['==', ['get', 'name'], 'Zona 3'], '#FF8E3D',
      '#428DFF'
    ];

    if (layer === 'ParkingZoneLoading') {
      return [
        ...filterArray,
        ...[filterStatus],
        ...defaultColorArray,
        ...[propFilterZoneLoading],
      ];
    }

    return defaultColorArray[0];
  }

  setLineMetrics(layer: string): boolean {
    if (layer === 'AgentsHistorical') {
      return true;
    }

    return false;
  }
  getLineWidth(layer: string): any | number {
    const interpolate = ['interpolate', ['linear'], ['zoom'], 8, 0.5, 22, 7];
    let defaultWidth = 1;

    if (layer === 'RoadRun') {
      defaultWidth = 1.5;
    }

    if (layer === 'Aforo') {
      return 7;
    }

    if (layer === 'TrafficSpeedRange' || layer === 'TrafficConcordance') {
      return interpolate;
    }

    if (layer === 'ParkingZone' || layer === 'MatrixOD' || layer === 'MatrixOD-camera') {
      return 2.5;
    }

    if (layer === 'BusZone-Lines') {
      return 2.5;
    }

    return defaultWidth;
  }

  getLineGradient(): any {
    const lineGradient = [
      'interpolate',
      ['linear'],
      ['line-progress'],
      0,
      'blue',
      0.1,
      'royalblue',
      0.3,
      'cyan',
      0.5,
      'lime',
      0.7,
      'yellow',
      1,
      'red',
    ];

    return lineGradient;
  }

  getClassLineColor(ruta: string): any {
    return `${ruta.toLowerCase()}-color`
  }

  setFilter(layer: string, filters: any): void {
    if (filters.length === 0) {
      this.map.setFilter(layer, null);
      return;
    }

    this.map.setFilter(layer, ['all', filters]);
  }

  setLayerVisibility(layer: string, visibility: boolean) {
    this.map.setLayoutProperty(
      layer,
      'visibility',
      visibility ? 'visible' : 'none'
    );
  }

  setLayerSource(source: any, layer: string) {
    const getSource: AnySourceImpl = this.map.getSource(layer);
    if (getSource) {
      const layerSource = {
        type: 'FeatureCollection',
        features: source,
      };
      (getSource as any).setData(layerSource);
    }
  }

  setPopup(layer: string) {
    const popup = new Popup({
      closeButton: false,
      closeOnClick: false,
    });

    this.map.on('mouseenter', layer, (e) => {
      this.map.getCanvas().style.cursor = 'pointer';

      if (
        layer !== 'TrafficSpeedRange-Lines' &&
        layer !== 'TrafficConcordance-Lines' &&
        layer !== 'action'
      ) {
        let coordinates;
        if (e.features[0].geometry.type === GeometryTypeEnum.Point) {
          coordinates = (e.features[0].geometry as any).coordinates.slice();

          while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
          }
        } else {
          coordinates = e.lngLat;
        }

        let html = '';
        if (layer === MapLayers.BusZone.toString()) {
          let htmlRoutes = `<div class='content-checks'>`;
          const rutas = JSON.parse(e.features[0].properties.rutas);

          rutas.forEach((ruta: any) => {
            htmlRoutes += `<div class='${this.getClassLineColor(ruta.split('_')[0])} item-check'> <span class='item-icon'>•</span> ${ruta.split('_')[0]}</div>`;
          });

          htmlRoutes += '</div>';

          html = `<div class='tooltip-title'>
            <img src="assets/icons/bus.svg" alt="Líneas de autobuses" class="icon-size-40">
            <span> ${e.features[0].properties.id}</span>
          </div>${htmlRoutes}`;

        } else {
          const popupText = layer === 'Accident' ? (JSON.parse(e.features[0].properties['0'])['DIRECCION']) : e.features[0].properties.markerPopupText;
          html = popupText ?? '-';
        }

        popup
          .setLngLat(coordinates as unknown as LngLat)
          .setHTML(html)
          .addTo(this.map);
      }

      if (
        layer === 'TrafficSpeedRange-Lines' ||
        layer === 'TrafficConcordance-Lines'
      ) {
        const features = this.map.queryRenderedFeatures(e.point, { layers: [layer] });
        if (features.length > 0) {

          const lineInfo = layer.includes('TrafficConcordance') || layer.includes('TrafficSpeedRange') ? features[0].properties.markerPopupText : features[0].properties.composed_name ||
            features[0].properties.street ||
            features[0].properties.nameTo ||
            features[0].properties.name ||
            features[0].properties.street;

          let coordinates;
          if (e.features[0].geometry.type === GeometryTypeEnum.Point) {
            coordinates = (e.features[0].geometry as any).coordinates.slice();

            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
              coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }
          } else {
            coordinates = e.lngLat;
          }
          popup
            .setLngLat(coordinates as unknown as LngLat)
            .setHTML(lineInfo)
            .addTo(this.map);
        }
      }

    });

    this.map.on('mouseleave', layer, () => {
      this.map.getCanvas().style.cursor = '';
      popup.remove();
    });
  }

  parkingZoneFilter(
    Source: {
      type: GeometryTypeEnum;
      markers: MarkerModelBase[];
      dataFeatures: any[];
    },
    layer: string, //Locations
    clickDispatcher: BehaviorSubject<any>,
    layerVisibility: boolean = true
  ): void {

    const parkingZoneMap: {
      [key: string]: (
        info: typeof Source,
        layer: string,
        clickDispatcher: BehaviorSubject<any>,
        layerVisibility: boolean
      ) => void
    } = {
      [ParkingGeoTypeEnum.PARKING_SPOT]: (info, layer, clickDispatcher, layerVisibility) => {
        const filteredData = (this.environment.citizen ?? false)
          ? info.dataFeatures.filter((e: any) => e.properties.status === "occupied" || e.properties.status === "free")
          : info.dataFeatures;

        info.dataFeatures = filteredData;
        this.addClusterLayerChart(info, layer, clickDispatcher, layerVisibility)
      },
      [ParkingGeoTypeEnum.ON_STREE_PARKING]: (info, layer, clickDispatcher, layerVisibility) =>
        this.addLines(info, layer, clickDispatcher, layerVisibility),
      [ParkingGeoTypeEnum.OFF_STREE_PARKING]: (info, layer, clickDispatcher, layerVisibility) =>
        this.addPointLayer(info, `${layer}-underground`, clickDispatcher, layerVisibility)
    }
    const rawType = Source.dataFeatures[0]?.properties?.type;
    const parkingZoneType = this.normalizeParkingGeoType(rawType);
    const parkingZoneHandler = parkingZoneMap[parkingZoneType];

    if (parkingZoneHandler) {
      parkingZoneHandler(Source, layer, clickDispatcher, layerVisibility);
    }
  }

  //TODO:"ParkingSpot" o la URL 
  private normalizeParkingGeoType(type: string): ParkingGeoTypeEnum | string {
    if (!type) return type;
    if (type === ParkingGeoTypeEnum.PARKING_SPOT || type === ParkingGeoTypeEnum.ON_STREE_PARKING || type === ParkingGeoTypeEnum.OFF_STREE_PARKING ||
        type === ParkingGeoTypeEnum.PARKING_LOADING_ZONE || type === ParkingGeoTypeEnum.PARKING_SPOT_LOADING) {
      return type;
    }
    if (typeof type === 'string' && type.includes('ParkingSpot')) {
      return ParkingGeoTypeEnum.PARKING_SPOT;
    }
    if (typeof type === 'string' && type.includes('OnStreetParking')) {
      return ParkingGeoTypeEnum.ON_STREE_PARKING;
    }
    if (typeof type === 'string' && type.includes('OffStreetParking')) {
      return ParkingGeoTypeEnum.OFF_STREE_PARKING;
    }
    return type;
  }

  parkingLoadingZoneFilter(
    Source: {
      type: GeometryTypeEnum;
      markers: MarkerModelBase[];
      dataFeatures: any[];
    },
    layer: string, //Locations
    clickDispatcher: BehaviorSubject<any>,
    layerVisibility: boolean = true
  ): void {

    const parkingZoneMap: {
      [key: string]: (
        info: typeof Source,
        layer: string,
        clickDispatcher: BehaviorSubject<any>,
        layerVisibility: boolean
      ) => void
    } = {
      [ParkingGeoTypeEnum.PARKING_LOADING_ZONE]: (info, layer, clickDispatcher, layerVisibility) =>
        this.addStatesLayer(info, layer, clickDispatcher, layerVisibility),
      [ParkingGeoTypeEnum.PARKING_SPOT]: (info, layer, clickDispatcher, layerVisibility) => {
        const filteredData = (this.environment.citizen ?? false)
          ? info.dataFeatures.filter((e: any) => e.properties.status === "occupied" || e.properties.status === "free")
          : info.dataFeatures;

        info.dataFeatures = filteredData;
        this.addClusterLayerChart(info, layer, clickDispatcher, layerVisibility)
      }
    }

    const rawType = Source.dataFeatures[0]?.properties?.type;
    const parkingZoneType = this.normalizeParkingGeoType(rawType);
    const parkingZoneHandler = parkingZoneMap[parkingZoneType];

    if (parkingZoneHandler) {
      parkingZoneHandler(Source, layer, clickDispatcher, layerVisibility);
    }

  }
}
